import { useFormContext } from '@components/form';
import { FormWithUnsavedChangesPrompt } from '@components/generic-modals';
import { HideContainer } from '@components/hide-container';
import { SelectOption } from '@components/select';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import {
  FieldType,
  MappableActionVariable,
  VariableValue,
} from '@components/text-editors/addons/field-mapping';
import { fieldTypeIconMap } from '@components/text-editors/addons/field-mapping-type-maps';
import { useScopedTranslate } from '@locale/translate';

import { useJourneyAction, useUpdateJourneyAction } from 'api/journey-actions/hooks';
import { Functions, JourneyActionDetailed } from 'api/types';
import { ACTION_TYPES } from 'ui/actions/action-config';
import {
  ActionFooter,
  ActionHeader,
  ActionSidebarLoading,
  DescriptionFormField,
  SidebarProps,
  createActionForm,
  useCreateActionFormStore,
  useVariableGroups,
} from 'ui/journey-builder/node-types/common';

import { functionLabel, FormatterInfoBox } from './helpers';

import { getFieldMappingId } from '../helpers';

const ACTION_TYPE = ACTION_TYPES.formatter;

type Data = {
  description: string;
  function: string;
  field: string;
};

const Form = createActionForm<Data>();

export const SidebarContent = (props: SidebarProps) => {
  const { journeyActionId } = props;
  const { data, isLoading } = useJourneyAction<typeof ACTION_TYPE>(journeyActionId);
  const { t } = useScopedTranslate(sidebarLocale);
  const formStore = useCreateActionFormStore();
  const fieldValue = formStore.useFieldValue('field') as VariableValue;
  const functionValue = formStore.useFieldValue('function') as Functions;
  const isDirty = formStore.useIsDirty();
  const fieldMappingId = getFieldMappingId(data?.journey ?? '');

  const editMutation = useUpdateJourneyAction(journeyActionId);

  const handleSave = async (data: Data) => {
    await editMutation.mutateAsync({
      description: data.description,
      isAutomatic: true,
      action: ACTION_TYPE,
      values: {
        function: data.function,
        field: data.field,
      },
    });
  };

  const fieldVariable = fieldValue?.variables
    ? (Object.values(fieldValue.variables)[0] as MappableActionVariable)
    : undefined;

  const fieldType = fieldVariable?.type;
  const variableGroups = useVariableGroups({
    actionVariables: data?.actionVariables,
    journeyId: data?.journey ?? '',
    excludedTypes: [],
  });

  if (isLoading || !data) {
    return <ActionSidebarLoading actionType={ACTION_TYPE} {...props} />;
  }

  return (
    <Sidebar>
      <ActionHeader actionType={ACTION_TYPE} {...props} />
      <Sidebar.Content>
        <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSave}>
          <Stack space={6}>
            <HideContainer title={t('actionDetails')}>
              <DescriptionFormField defaultValue={data.description} />
            </HideContainer>
            <Sidebar.Separator />
            <HideContainer title={t('configuration')}>
              <Stack space={4}>
                <Form.FieldMappingField
                  id="field"
                  label={t('field')}
                  variableGroups={variableGroups}
                  defaultValue={data.values?.field}
                  singleLine
                  plaintext
                  validators={['required']}
                  info={t('fieldInfo')}
                  fieldMappingId={fieldMappingId}
                />
                <FunctionsForField
                  key={fieldValue?.value}
                  fieldType={fieldType}
                  defaultValue={data.values?.function}
                />
              </Stack>
            </HideContainer>
            <Sidebar.Separator />
            <HideContainer title={t('formatterOutput')}>
              <Stack space={2}>
                <Text text={t('output')} font="semibold14" />
                {functionValue && fieldValue?.value ? (
                  <Output fieldVariable={fieldVariable} data={data} functionValue={functionValue} />
                ) : (
                  <Text text={t('selectToSeeOutput')} font="regular14" color="textHelper" />
                )}
              </Stack>
            </HideContainer>
            <Sidebar.Separator />
          </Stack>
          <ActionFooter action={data} isDisabled={!isDirty || editMutation.isLoading} form={Form} />
        </FormWithUnsavedChangesPrompt>
      </Sidebar.Content>
    </Sidebar>
  );
};

export const FunctionsForField = ({
  fieldType,
  defaultValue,
  defaultLeapYear,
}: {
  fieldType?: FieldType;
  defaultValue?: string;
  defaultLeapYear?: boolean;
}) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { store: formStore } = useFormContext();

  const functionOptions = (fieldType?: FieldType): SelectOption[] => {
    switch (fieldType) {
      case 'array':
        return [
          {
            value: 'first_item_of_array',
            label: functionLabel('first_item_of_array'),
          },
          {
            value: 'last_item_of_array',
            label: functionLabel('last_item_of_array'),
          },
          {
            value: 'random_item_of_array',
            label: functionLabel('random_item_of_array'),
          },
          {
            value: 'length_of_array',
            label: functionLabel('length_of_array'),
          },
        ];
      case 'file':
        return [
          {
            value: 'digest_sha256_file',
            label: functionLabel('digest_sha256_file'),
          },
          {
            value: 'file_extension',
            label: functionLabel('file_extension'),
          },
        ];
      case 'date':
        return [
          {
            value: 'date_month_future_date',
            label: functionLabel('date_month_future_date'),
          },
        ];
      default:
        return [];
    }
  };
  const options = functionOptions(fieldType);
  const functionValue = formStore.useFieldValue('function') as Functions;

  return (
    <>
      <Form.Select
        id="function"
        options={options}
        label={t('function')}
        placeholder={
          fieldType
            ? options.length > 0
              ? t('selectFunction')
              : t('noFunctionsAvailable')
            : t('selectField')
        }
        defaultValue={defaultValue}
        noOptions={() => t('noFunctionsAvailable')}
        validators={['required']}
      />
      {functionValue === 'date_month_future_date' && (
        <FormatterInfoBox infoType="dateMonthFutureDateInfo" />
      )}
    </>
  );
};

const Output = ({
  fieldVariable,
  data,
  functionValue,
}: {
  fieldVariable?: MappableActionVariable;
  data: JourneyActionDetailed;
  functionValue: Functions;
}) => {
  const { t } = useScopedTranslate(sidebarLocale);
  // When we support more formatting options, we can make this more dynamic
  const fieldAction = fieldVariable?.action;
  const fieldField = fieldVariable?.field ?? '';
  const actionVariables = data.actionVariables.find((variable) => variable.shortId === fieldAction);
  let arrayTemplate = actionVariables?.fields?.[fieldField]?.template ?? [];
  if (arrayTemplate.length === 0 || functionValue === 'length_of_array') {
    arrayTemplate = [
      {
        label: t('value'),
        type: 'text',
      },
    ];
  }
  return (
    <>
      {arrayTemplate.map((item) => {
        const Icon = fieldTypeIconMap[item.type ?? 'text'];
        return (
          <Stack key={item.label} space={2} orientation="horizontal" align="center">
            {Icon && <Icon color="elementPurpleInvert" />}
            <Text text={item.label} font="regular14" />
          </Stack>
        );
      })}
    </>
  );
};

const sidebarLocale = {
  en: {
    actionDetails: 'Action details',
    configuration: 'Configuration',
    formatterOutput: 'Formatter output',
    output: 'Output',
    selectToSeeOutput: 'Select a function to see output',
    function: 'Function',
    selectFunction: 'Select function',
    noFunctionsAvailable: 'No functions available for selected field',
    selectField: 'Select field for available functions',
    field: 'Field',
    fieldInfo: 'Field to format',
    value: 'Value',
  },
};
